import React from 'react';
import { Title, Text, Button, Progress } from '@mantine/core';
import { Coffee, BrainCircuit, Music, RefreshCcw } from 'lucide-react';

const BreakModal = ({ 
  className = ""
}) => {
  return (
      <div className={`mt-4 w-full max-w-md mx-auto bg-white rounded-xl transform transition-all ${className}`}>
        {/* Top gradient bar */}
        <div className="h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-t-xl"></div>
        
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Title order={3} className="text-indigo-900 font-bold">
              Break Time!
            </Title>
          </div>
          
          {/* Coffee Cup Illustration */}
          <div className="flex justify-center mb-6">
            <svg viewBox="0 0 400 200" className="w-full h-48">
              {/* Background with soft gradient */}
              <defs>
                <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#f8faff" />
                  <stop offset="100%" stopColor="#f0f4ff" />
                </linearGradient>
                <radialGradient id="steamGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                  <stop offset="0%" stopColor="white" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="white" stopOpacity="0" />
                </radialGradient>
              </defs>
              
              {/* Background */}
              <rect width="400" height="200" fill="url(#bgGradient)" rx="12" />
              
              {/* Table/Surface */}
              <rect x="100" y="140" width="200" height="6" fill="#94a3b8" rx="2" />
              <rect x="90" y="146" width="220" height="30" fill="#64748b" rx="2" />
              <rect x="90" y="146" width="220" height="10" fill="#475569" rx="1" opacity="0.3" />
              
              {/* Saucer */}
              <ellipse cx="200" cy="140" rx="60" ry="12" fill="#e2e8f0" />
              <ellipse cx="200" cy="140" rx="50" ry="8" fill="#f1f5f9" />
              
              {/* Coffee Cup */}
              <g>
                {/* Cup body */}
                <path d="M160,60 C160,60 155,100 155,120 C155,132 175,140 200,140 C225,140 245,132 245,120 C245,100 240,60 240,60 Z" fill="#f8fafc" />
                
                {/* Inner cup shadow/coffee */}
                <ellipse cx="200" cy="60" rx="40" ry="10" fill="#7c2d12" />
                <path d="M165,65 C165,65 170,100 170,115 C170,125 185,130 200,130 C215,130 230,125 230,115 C230,100 235,65 235,65 Z" fill="#92400e" />
                <ellipse cx="200" cy="65" rx="35" ry="8" fill="#c2410c" />
                
                {/* Cup rim */}
                <ellipse cx="200" cy="60" rx="40" ry="10" fill="#f8fafc" />
                <ellipse cx="200" cy="60" rx="35" ry="8" fill="#7c2d12" />
                
                {/* Cup handle */}
                <path d="M240,70 C260,75 260,100 240,105" fill="none" stroke="#f8fafc" strokeWidth="8" strokeLinecap="round" />
                
                {/* Steam */}
                <path d="M180,40 C175,30 185,20 180,10" stroke="white" strokeWidth="3" strokeLinecap="round" opacity="0.6">
                  <animate attributeName="opacity" values="0.6;0.2;0.6" dur="3s" repeatCount="indefinite" />
                  <animate attributeName="d" values="M180,40 C175,30 185,20 180,10;M180,40 C185,30 175,20 180,10;M180,40 C175,30 185,20 180,10" dur="3s" repeatCount="indefinite" />
                </path>
                <path d="M200,35 C195,25 205,15 200,5" stroke="white" strokeWidth="3" strokeLinecap="round" opacity="0.6">
                  <animate attributeName="opacity" values="0.2;0.7;0.2" dur="4s" repeatCount="indefinite" />
                  <animate attributeName="d" values="M200,35 C195,25 205,15 200,5;M200,35 C205,25 195,15 200,5;M200,35 C195,25 205,15 200,5" dur="4s" repeatCount="indefinite" />
                </path>
                <path d="M220,40 C215,30 225,20 220,10" stroke="white" strokeWidth="3" strokeLinecap="round" opacity="0.6">
                  <animate attributeName="opacity" values="0.4;0.1;0.4" dur="3.5s" repeatCount="indefinite" />
                  <animate attributeName="d" values="M220,40 C215,30 225,20 220,10;M220,40 C225,30 215,20 220,10;M220,40 C215,30 225,20 220,10" dur="3.5s" repeatCount="indefinite" />
                </path>
                
                {/* Highlight on cup */}
                <path d="M175,70 C180,80 190,90 200,90 C210,90 220,80 225,70" fill="none" stroke="white" strokeWidth="2" opacity="0.4" />
              </g>
              
              {/* Coffee beans scattered around */}
              <ellipse cx="130" cy="150" rx="8" ry="5" transform="rotate(-30,130,150)" fill="#7c2d12" />
              <ellipse cx="265" cy="155" rx="8" ry="5" transform="rotate(20,265,155)" fill="#7c2d12" />
              <ellipse cx="145" cy="165" rx="7" ry="4" transform="rotate(15,145,165)" fill="#854d0e" />
              <ellipse cx="240" cy="160" rx="7" ry="4" transform="rotate(-15,240,160)" fill="#854d0e" />
              <ellipse cx="200" cy="170" rx="8" ry="5" transform="rotate(45,200,170)" fill="#7c2d12" />
            </svg>
          </div>
          
          {/* Break tips */}
          <div className="mb-6">
            <Text className="text-gray-700 font-medium mb-4">
              Tips to maximize your break:
            </Text>
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-600 mt-0.5 mr-3">
                  <Coffee size={14} />
                </div>
                <Text size="sm" className="text-gray-600">
                  Hydrate with water or enjoy a quick cup of coffee
                </Text>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-purple-100 text-purple-600 mt-0.5 mr-3">
                  <BrainCircuit size={14} />
                </div>
                <Text size="sm" className="text-gray-600">
                  Do a quick mental reset with deep breathing or meditation
                </Text>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-pink-100 text-pink-600 mt-0.5 mr-3">
                  <Music size={14} />
                </div>
                <Text size="sm" className="text-gray-600">
                  Listen to calming music to reduce test anxiety
                </Text>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-amber-100 text-amber-600 mt-0.5 mr-3">
                  <RefreshCcw size={14} />
                </div>
                <Text size="sm" className="text-gray-600">
                  Stand up and stretch to refresh your body and mind
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default BreakModal;
